import { useIsVisible } from "@website/hooks/is-visible";
import { useViewPort } from "@website/hooks/viewport";
import { Chips } from "@website/ui/chips";
import { holidaysList } from "@website/ui/datepicker/components/main/holidays";
import styles from "@website/ui/datepicker/components/main/main.module.scss";
import { MainPropertiesType } from "@website/ui/datepicker/components/main/main.types";
import { Table } from "@website/ui/datepicker/components/table";
import { convertDate } from "@website/ui/datepicker/datepicker.utils";
import { dateFormat } from "@website/utils/date-format";
import { useRouter } from "next/router";
import { FC, Fragment, memo, useEffect, useMemo } from "react";

const Main: FC<MainPropertiesType> = (properties) => {
  const {
    type,
    calendarLocale,
    value,
    setValue,
    onChange,
    showDates,
    increaseShowDatesNumber,
    scrollReference,
    setScrollReference,
    calendarData,
    mode,
    disableLimit,
    view,
    suggestions,
    disableDates,
    isLabelTop
  } = properties;
  const router = useRouter();
  const { reference, inView } = useIsVisible({
    root: scrollReference,
    rootMargin: "50px"
  });
  const { isDesktop } = useViewPort();

  useEffect(() => {
    if (!isDesktop && inView && showDates.length < 12) {
      increaseShowDatesNumber();
    }
  }, [isDesktop, inView, showDates, increaseShowDatesNumber]);

  const calendarInfo = useMemo(
    () => ({
      findDisableDay: true,
      selectedDate: undefined
    }),
    []
  );

  const suggestedDates =
    suggestions?.filter((date) => {
      const dateFrom = convertDate(
        { dateTime: date?.date_from },
        calendarLocale
      );
      const dateTo = convertDate({ dateTime: date?.date_to }, calendarLocale);
      return (
        (dateFrom.year === showDates?.[0]?.year &&
          dateFrom.month === showDates?.[0]?.month) ||
        (dateTo.year === showDates?.[0]?.year &&
          dateTo.month === showDates?.[0]?.month)
      );
    }) ?? [];

  return (
    <div
      className={`${styles["main"]} ${
        view === "mobile" ? styles["main__mobile"] : styles["main__default"]
      } ${styles["rtl" || "ltr"]}`}
    >
      <div
        className={`${styles["tables-container"]} ${
          view === "mobile" ? "" : styles["tables-container__default"]
        }`}
        ref={view === "mobile" ? setScrollReference : null}
        id="main-calendar"
      >
        {showDates.map((item, index) => (
          <Fragment key={`${item.year}_${item.month}`}>
            {index > 0 ? (
              <div
                className={`${styles["divider"]} ${
                  view === "mobile"
                    ? styles["divider__mobile"]
                    : styles["divider__default"]
                }`}
              />
            ) : null}

            <Table
              calendarData={calendarData}
              calendarInfo={calendarInfo}
              calendarLocale={calendarLocale}
              disableDates={disableDates}
              disableLimit={disableLimit}
              holidays={holidaysList}
              isLast={showDates.length === index + 1}
              mode={mode}
              month={item.month}
              onChange={onChange}
              setValue={setValue}
              suggestedDates={suggestedDates}
              type={type}
              value={value}
              view={view}
              year={item.year}
              isLabelTop={isLabelTop}
            />
            {suggestedDates?.length > 0 && (
              <div className="flex flex-col gap-1 self-start">
                <div className="flex flex-row items-center gap-4">
                  <div className="border-t-warning mt-1 border-y-8 border-r-8 border-b-transparent border-r-transparent" />
                  <div className="text-caption">بازه‌های اقامتی مشخص:</div>
                </div>
                <div className="flex flex-row flex-wrap gap-2">
                  {suggestedDates?.map((date) => (
                    <Chips
                      color={
                        value?.[0]?.dateTime === date?.date_from &&
                        value?.[1]?.dateTime === date?.date_to
                          ? "secondary"
                          : ""
                      }
                      onClick={() => {
                        const dateFrom = convertDate(
                          { dateTime: date.date_from },
                          calendarLocale
                        );
                        const dateTo = convertDate(
                          { dateTime: date.date_to },
                          calendarLocale
                        );
                        if (
                          calendarData[date?.date_from] &&
                          calendarData[date?.date_to] &&
                          calendarData?.[date?.date_from]?.state !==
                            "disabled" &&
                          value.every(
                            (item) =>
                              calendarData?.[item?.dateTime]?.state !== "rack"
                          )
                        ) {
                          setValue([
                            {
                              year: dateFrom?.year,
                              month: dateFrom?.month,
                              day: dateFrom?.day,
                              dateTime: date?.date_from
                            },
                            {
                              year: dateTo?.year,
                              month: dateTo?.month,
                              day: dateTo?.day,
                              dateTime: date?.date_to
                            }
                          ]);
                          return router.replace(
                            {
                              pathname: router.pathname,
                              query: {
                                ...router.query,
                                date_from: date?.date_from,
                                date_to: date?.date_to
                              }
                            },
                            undefined,
                            { shallow: true, scroll: false }
                          );
                        }
                      }}
                      selected={
                        value?.[0]?.dateTime === date?.date_from &&
                        value?.[1]?.dateTime === date?.date_to
                      }
                      text={`${dateFormat(date?.date_from, {
                        locale: router.locale,
                        template: "dd MMMM"
                      })} تا ${dateFormat(date?.date_to, {
                        locale: router.locale,
                        template: "dd MMMM"
                      })}`}
                      variant={
                        value?.[0]?.dateTime === date?.date_from &&
                        value?.[1]?.dateTime === date?.date_to
                          ? "contained"
                          : "outline"
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </Fragment>
        ))}
        {type === "popover" && (
          <div className={styles["scroll-more-area"]} ref={reference} />
        )}
      </div>
    </div>
  );
};

const MainMemoized = memo(Main);
export { MainMemoized as Main };
